import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  FileText, ChevronLeft, Loader2, Clock, CheckCircle, 
  Mail, Send, Download, Eye, AlertTriangle, User, Phone, 
  MessageSquare, DollarSign, Calculator, Upload, FileSpreadsheet, FileDown, Settings, Package, Building2, X, Archive, ArchiveRestore, RefreshCw, RotateCcw, CalendarClock, Plus
} from 'lucide-react';

const AdminQuoteRequests = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const csvInputRef = useRef(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [sendQuoteOpen, setSendQuoteOpen] = useState(false);
  const [acceptOnBehalfOpen, setAcceptOnBehalfOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [acceptNotes, setAcceptNotes] = useState('');
  const [acceptPoNumber, setAcceptPoNumber] = useState('');
  const [proofFile, setProofFile] = useState(null);
  
  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pricing data
  const [materials, setMaterials] = useState([]);
  const [postProcesses, setPostProcesses] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  
  const [quoteData, setQuoteData] = useState({
    total: '',
    message: ''
  });

  // Handle URL parameter to auto-open a specific quote request
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && requests.length > 0) {
      const requestToOpen = requests.find(r => r.request_id === highlightId);
      if (requestToOpen) {
        viewDetails(requestToOpen);
        // Clear highlight but keep company filter
        const company = searchParams.get('company');
        if (company) {
          setSearchParams({ company });
        } else {
          setSearchParams({});
        }
      }
    }
  }, [requests]);

  useEffect(() => {
    fetchRequests();
    fetchPricingData();
  }, [filter, showArchived, searchParams]);

  const fetchPricingData = async () => {
    try {
      const [matRes, procRes, delRes] = await Promise.all([
        axios.get(`${API}/settings/materials`),
        axios.get(`${API}/settings/post-processes`),
        axios.get(`${API}/settings/delivery-options`)
      ]);
      setMaterials(matRes.data.materials || []);
      setPostProcesses(procRes.data.post_processes || []);
      setDeliveryOptions(delRes.data.delivery_options || []);
    } catch (error) {
      console.error('Failed to load pricing data');
    }
  };

  const fetchRequests = async () => {
    try {
      const companyId = searchParams.get('company');
      let params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (companyId) params.append('company', companyId);
      if (showArchived) params.append('include_archived', 'true');
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/admin/quote-requests${queryString}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setRequests(response.data.quote_requests || []);
      
      // If filtering by company, fetch company info
      if (companyId) {
        setCompanyFilter(companyId);
        try {
          const companyRes = await axios.get(`${API}/admin/companies/${companyId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
          });
          setCompanyInfo(companyRes.data);
        } catch (e) {
          console.error('Failed to fetch company info');
        }
      } else {
        setCompanyFilter(null);
        setCompanyInfo(null);
      }
    } catch (error) {
      toast.error('Failed to fetch quote requests');
    }
    setLoading(false);
  };

  const clearCompanyFilter = () => {
    setSearchParams({});
    setCompanyFilter(null);
    setCompanyInfo(null);
  };

  const viewDetails = async (request) => {
    try {
      const response = await axios.get(`${API}/admin/quote-requests/${request.request_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setSelectedRequest(response.data);
      setDetailsOpen(true);
    } catch (error) {
      toast.error('Failed to load request details');
    }
  };

  const updateStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/quote-requests/${requestId}`, 
        { status },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success('Status updated');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const archiveRequest = async (requestId, archive = true) => {
    try {
      await axios.put(`${API}/admin/quote-requests/${requestId}`, 
        { archived: archive },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success(archive ? 'Quote archived' : 'Quote restored');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update quote');
    }
  };

  // Reactivate dialog state
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [reactivateRequest, setReactivateRequest] = useState(null);
  const [reactivatePrice, setReactivatePrice] = useState('');
  const [updatePrice, setUpdatePrice] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const openReactivateDialog = (req) => {
    setReactivateRequest(req);
    setReactivatePrice(req.quoted_total?.toString() || '');
    setUpdatePrice(false);
    setReactivateDialogOpen(true);
  };

  const reactivateQuote = async () => {
    if (!reactivateRequest) return;
    
    setReactivating(true);
    try {
      const payload = {};
      if (updatePrice && reactivatePrice) {
        payload.new_total = parseFloat(reactivatePrice);
      }
      
      await axios.post(`${API}/admin/quote-requests/${reactivateRequest.request_id}/reactivate`, 
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      toast.success('Quote reactivated! Valid for another 30 days.');
      setReactivateDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate quote');
    } finally {
      setReactivating(false);
    }
  };

  // Calculate price for a single file
  const calculateFilePrice = (file) => {
    const volume = parseFloat(file.volume) || 0;
    const quantity = parseInt(file.quantity) || 1;
    
    // Get material price
    const material = materials.find(m => m.material_id === file.material);
    const pricePerCm3 = material?.price_per_cm3 || 0;
    
    // Base price
    let unitPrice = volume * pricePerCm3;
    
    // Add post-processing costs
    if (file.postProcesses && file.postProcesses.length > 0) {
      for (const procId of file.postProcesses) {
        const proc = postProcesses.find(p => p.process_id === procId);
        if (proc) {
          unitPrice += proc.price_per_unit || 0;
        }
      }
    }
    
    return {
      unitPrice: unitPrice,
      totalPrice: unitPrice * quantity,
      volume,
      quantity,
      materialName: material?.name || 'Unknown',
      pricePerCm3
    };
  };

  // Calculate total quote
  const calculateQuoteTotal = () => {
    if (!selectedRequest?.files) return { subtotal: 0, delivery: 0, total: 0, items: [] };
    
    const items = selectedRequest.files.map(file => ({
      ...file,
      pricing: calculateFilePrice(file)
    }));
    
    const subtotal = items.reduce((sum, item) => sum + item.pricing.totalPrice, 0);
    
    // Get delivery cost
    const delivery = deliveryOptions.find(d => d.delivery_id === selectedRequest.delivery_option_id);
    const deliveryCost = delivery?.price || 0;
    
    return {
      subtotal,
      delivery: deliveryCost,
      total: subtotal + deliveryCost,
      items
    };
  };

  const updateFileVolume = (fileIndex, volume) => {
    if (!selectedRequest) return;
    
    const updatedFiles = [...selectedRequest.files];
    updatedFiles[fileIndex] = {
      ...updatedFiles[fileIndex],
      volume: parseFloat(volume) || 0
    };
    
    setSelectedRequest({
      ...selectedRequest,
      files: updatedFiles
    });
  };

  const updateFileMaterial = (fileIndex, materialId) => {
    if (!selectedRequest) return;
    
    const updatedFiles = [...selectedRequest.files];
    updatedFiles[fileIndex] = {
      ...updatedFiles[fileIndex],
      material: materialId
    };
    
    setSelectedRequest({
      ...selectedRequest,
      files: updatedFiles
    });
  };

  // CSV Upload Handler
  const handleCsvUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        if (typeof text !== 'string') return;
        
        // Parse CSV
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          toast.error('CSV file is empty or has no data rows');
          return;
        }
        
        // Get header row
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const nameIndex = headers.findIndex(h => 
          h.includes('name') || h.includes('part') || h.includes('file')
        );
        const volumeIndex = headers.findIndex(h => 
          h.includes('volume') || h.includes('cm3') || h.includes('cm³')
        );
        
        if (nameIndex === -1 || volumeIndex === -1) {
          toast.error('CSV must have "Part Name" and "Volume" columns');
          return;
        }
        
        // Parse data rows into a map
        const volumeMap = new Map();
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          const partName = values[nameIndex];
          const volume = parseFloat(values[volumeIndex]);
          
          if (partName && !isNaN(volume) && volume > 0) {
            volumeMap.set(partName.toLowerCase(), volume);
          }
        }
        
        if (volumeMap.size === 0) {
          toast.error('No valid volume data found in CSV');
          return;
        }
        
        // Match and update files
        let matchCount = 0;
        const updatedFiles = selectedRequest.files.map(file => {
          const fileName = file.name?.toLowerCase() || '';
          
          // Try exact match first
          if (volumeMap.has(fileName)) {
            matchCount++;
            return { ...file, volume: volumeMap.get(fileName) };
          }
          
          // Try matching without extension
          const fileNameNoExt = fileName.replace(/\.[^/.]+$/, '');
          if (volumeMap.has(fileNameNoExt)) {
            matchCount++;
            return { ...file, volume: volumeMap.get(fileNameNoExt) };
          }
          
          // Try partial match (CSV name contains file name or vice versa)
          for (const [csvName, volume] of volumeMap.entries()) {
            const csvNameNoExt = csvName.replace(/\.[^/.]+$/, '');
            if (fileNameNoExt.includes(csvNameNoExt) || csvNameNoExt.includes(fileNameNoExt)) {
              matchCount++;
              return { ...file, volume };
            }
          }
          
          return file;
        });
        
        setSelectedRequest({
          ...selectedRequest,
          files: updatedFiles
        });
        
        if (matchCount > 0) {
          toast.success(`Matched ${matchCount} of ${selectedRequest.files.length} files from CSV`);
        } else {
          toast.warning('No files matched. Check that part names in CSV match file names.');
        }
        
      } catch (error) {
        console.error('CSV parse error:', error);
        toast.error('Failed to parse CSV file');
      }
    };
    
    reader.readAsText(file);
    // Reset input so same file can be uploaded again
    event.target.value = '';
  };

  const saveFileVolumes = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      // Calculate and save the quoted total along with files
      const quoteCalc = calculateQuoteTotal();
      
      await axios.put(`${API}/admin/quote-requests/${selectedRequest.request_id}`, 
        { 
          files: selectedRequest.files,
          quoted_total: quoteCalc.total,
          quote_breakdown: {
            items: quoteCalc.items.map(item => ({
              name: item.name,
              volume: item.pricing.volume,
              quantity: item.pricing.quantity,
              material: item.pricing.materialName,
              unit_price: item.pricing.unitPrice,
              total_price: item.pricing.totalPrice
            })),
            subtotal: quoteCalc.subtotal,
            delivery: quoteCalc.delivery,
            total: quoteCalc.total
          }
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success('Quote saved');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to save quote');
    }
    setProcessing(false);
  };

  const downloadPDF = async (requestId) => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}/admin/quote-requests/${requestId}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${requestId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const downloadAllFiles = async (requestId) => {
    try {
      toast.info('Preparing download...');
      const response = await axios.get(`${API}/admin/quote-requests/${requestId}/download-all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        responseType: 'blob'
      });
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${requestId}_files.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Downloaded all files');
    } catch (error) {
      toast.error('Failed to download files');
    }
  };

  const openSendQuote = (request) => {
    setSelectedRequest(request);
    // Pre-populate with saved quoted_total or calculate from current data
    const savedTotal = request.quoted_total;
    setQuoteData({ 
      total: savedTotal ? savedTotal.toFixed(2) : '', 
      message: '' 
    });
    setSendQuoteOpen(true);
  };

  const openSendQuoteFromDetails = () => {
    // Calculate current total and open send dialog
    const quoteCalc = calculateQuoteTotal();
    setQuoteData({ 
      total: quoteCalc.total.toFixed(2), 
      message: '' 
    });
    setDetailsOpen(false);
    setSendQuoteOpen(true);
  };

  const sendQuote = async () => {
    if (!quoteData.total || parseFloat(quoteData.total) <= 0) {
      toast.error('Please enter a valid quote total');
      return;
    }

    setProcessing(true);
    try {
      // Get the breakdown from the saved request or calculate fresh
      const quoteCalc = calculateQuoteTotal();
      
      await axios.post(`${API}/admin/quote-requests/${selectedRequest.request_id}/send-quote`, 
        { 
          quoted_total: parseFloat(quoteData.total),
          message: quoteData.message,
          quote_breakdown: {
            items: quoteCalc.items.map(item => ({
              name: item.name,
              volume: item.pricing.volume,
              quantity: item.pricing.quantity,
              material: item.pricing.materialName,
              unit_price: item.pricing.unitPrice,
              total_price: item.pricing.totalPrice
            })),
            subtotal: quoteCalc.subtotal,
            delivery: quoteCalc.delivery
          }
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success('Quote sent to customer!');
      setSendQuoteOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quote');
    }
    setProcessing(false);
  };

  const handleAcceptOnBehalf = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('notes', acceptNotes);
      formData.append('po_number', acceptPoNumber);
      if (proofFile) {
        formData.append('proof_file', proofFile);
      }
      
      const response = await axios.post(
        `${API}/admin/quote-requests/${selectedRequest.request_id}/accept-on-behalf`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(`Quote accepted! Invoice sent to ${selectedRequest.customer_email}. Order: ${response.data.order_id}`);
      setAcceptOnBehalfOpen(false);
      setAcceptNotes('');
      setAcceptPoNumber('');
      setProofFile(null);
      setDetailsOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept quote');
    }
    setProcessing(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      reviewing: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      quoted: 'bg-green-500/10 text-green-500 border-green-500/20',
      completed: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
      cancelled: 'bg-red-500/10 text-red-500 border-red-500/20'
    };
    return styles[status] || styles.pending;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Sorting logic
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedRequests = [...requests].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortField) {
      case 'request_id':
        aVal = a.request_id || '';
        bVal = b.request_id || '';
        break;
      case 'customer_name':
        aVal = a.customer_name?.toLowerCase() || '';
        bVal = b.customer_name?.toLowerCase() || '';
        break;
      case 'customer_email':
        aVal = a.customer_email?.toLowerCase() || '';
        bVal = b.customer_email?.toLowerCase() || '';
        break;
      case 'files':
        aVal = a.files?.length || 0;
        bVal = b.files?.length || 0;
        break;
      case 'quoted_total':
        aVal = a.quoted_total || 0;
        bVal = b.quoted_total || 0;
        break;
      case 'status':
        aVal = a.status || '';
        bVal = b.status || '';
        break;
      case 'created_at':
      default:
        aVal = new Date(a.created_at || 0).getTime();
        bVal = new Date(b.created_at || 0).getTime();
        break;
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-white/50 hover:text-white">
                <ChevronLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white">Quote Requests</h1>
                <p className="text-white/50 text-sm">Review and respond to customer quote requests</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/admin/pdf-settings">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  <Settings className="w-4 h-4 mr-2" />
                  PDF Settings
                </Button>
              </Link>
              <Select value={sortField} onValueChange={(v) => { setSortField(v); }}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white w-40">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10">
                  <SelectItem value="created_at" className="text-white">Date</SelectItem>
                  <SelectItem value="customer_name" className="text-white">Customer</SelectItem>
                  <SelectItem value="quoted_total" className="text-white">Total</SelectItem>
                  <SelectItem value="files" className="text-white">Files</SelectItem>
                  <SelectItem value="status" className="text-white">Status</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="icon"
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortDirection === 'asc' ? '↑' : '↓'}
              </Button>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10">
                  <SelectItem value="all" className="text-white">All Quotes</SelectItem>
                  <SelectItem value="pending" className="text-white">⏳ Pending</SelectItem>
                  <SelectItem value="reviewing" className="text-white">🔍 Reviewing</SelectItem>
                  <SelectItem value="quoted" className="text-white">💰 Quoted</SelectItem>
                  <SelectItem value="accepted" className="text-white">✅ Accepted</SelectItem>
                  <SelectItem value="declined" className="text-white">❌ Declined</SelectItem>
                  <SelectItem value="expired" className="text-white">⏰ Expired</SelectItem>
                  <SelectItem value="completed" className="text-white">📦 Completed</SelectItem>
                  <SelectItem value="cancelled" className="text-white">🚫 Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-sm">
                <Switch
                  id="show-archived"
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                  className="data-[state=checked]:bg-[#CCFF00]"
                />
                <Label htmlFor="show-archived" className="text-white/70 text-sm cursor-pointer whitespace-nowrap">
                  Show Archived
                </Label>
              </div>
              <Button 
                variant="outline" 
                size="icon"
                className="border-white/20 text-white hover:bg-white/10"
                onClick={fetchRequests}
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Link to="/admin/create-quote">
                <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Company Filter Banner */}
          {companyFilter && companyInfo && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-purple-500" />
                  <div>
                    <h3 className="text-purple-400 font-bold">Filtering by Company: {companyInfo.name}</h3>
                    <p className="text-white/50 text-sm">
                      Showing {requests.length} quote request(s) from {companyInfo.members?.length || 0} company member(s)
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/admin/orders?company=${companyFilter}`}>
                    <Button size="sm" variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 rounded-none">
                      <Package className="w-4 h-4 mr-2" />
                      View Orders
                    </Button>
                  </Link>
                  <Link to={`/admin/companies`}>
                    <Button size="sm" variant="outline" className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 rounded-none">
                      <Building2 className="w-4 h-4 mr-2" />
                      Back to Companies
                    </Button>
                  </Link>
                  <Button size="sm" variant="ghost" onClick={clearCompanyFilter} className="text-white/50 hover:text-white">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Requests List */}
          <div className="bg-[#111] border border-white/10 rounded-sm">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
              </div>
            ) : sortedRequests.length === 0 ? (
              <div className="text-center py-16">
                <Mail className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No quote requests found</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {sortedRequests.map((req) => (
                  <div key={req.request_id} className={`p-6 hover:bg-white/5 transition-colors ${req.archived ? 'opacity-60 bg-white/[0.02]' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-[#CCFF00] font-mono font-bold">{req.request_id}</span>
                          <Badge variant="outline" className={getStatusBadge(req.status)}>
                            {req.status}
                          </Badge>
                          {req.archived && (
                            <Badge variant="outline" className="border-orange-500/50 text-orange-400 bg-orange-500/10">
                              <Archive className="w-3 h-3 mr-1" />
                              Archived
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-6 text-sm text-white/70 mb-3">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {req.customer_name || 'No name'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {req.customer_email}
                          </span>
                          {req.customer_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {req.customer_phone}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-white/50">
                            <FileText className="w-4 h-4 inline mr-1" />
                            {req.files?.length || 0} file(s)
                          </span>
                          <span className="text-white/50">
                            <Clock className="w-4 h-4 inline mr-1" />
                            {formatDate(req.created_at)}
                          </span>
                          {req.quoted_total && (
                            <span className="text-[#CCFF00] font-mono">
                              <DollarSign className="w-4 h-4 inline" />
                              {req.quoted_total.toFixed(2)}
                            </span>
                          )}
                          {/* Show expiry date for quoted items */}
                          {req.status === 'quoted' && req.expires_at && (
                            <span className={`flex items-center gap-1 ${
                              new Date(req.expires_at) < new Date() 
                                ? 'text-red-400' 
                                : new Date(req.expires_at) < new Date(Date.now() + 7*24*60*60*1000)
                                  ? 'text-yellow-400'
                                  : 'text-white/50'
                            }`}>
                              <CalendarClock className="w-4 h-4" />
                              Expires {formatDate(req.expires_at)}
                            </span>
                          )}
                          {/* Show expired notice */}
                          {req.status === 'expired' && (
                            <span className="text-red-400 flex items-center gap-1">
                              <AlertTriangle className="w-4 h-4" />
                              Expired {req.expired_at ? formatDate(req.expired_at) : ''}
                            </span>
                          )}
                        </div>

                        {req.customer_notes && (
                          <div className="mt-3 p-2 bg-white/5 rounded text-sm text-white/70">
                            <MessageSquare className="w-4 h-4 inline mr-2 text-white/30" />
                            {req.customer_notes}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => downloadAllFiles(req.request_id)}
                          className="text-[#CCFF00] hover:text-[#CCFF00] hover:bg-[#CCFF00]/10"
                          title="Download Files"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {req.quoted_total > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => downloadPDF(req.request_id)}
                            className="text-purple-400 hover:text-purple-400 hover:bg-purple-400/10"
                            title="Download PDF Quote"
                          >
                            <FileDown className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewDetails(req)}
                          className="text-white/70 hover:text-white"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        
                        {/* Archive/Restore button */}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => archiveRequest(req.request_id, !req.archived)}
                          className={req.archived 
                            ? "text-green-400 hover:text-green-400 hover:bg-green-400/10" 
                            : "text-orange-400 hover:text-orange-400 hover:bg-orange-400/10"
                          }
                          title={req.archived ? "Restore from Archive" : "Archive Quote"}
                        >
                          {req.archived ? (
                            <ArchiveRestore className="w-4 h-4" />
                          ) : (
                            <Archive className="w-4 h-4" />
                          )}
                        </Button>
                        
                        {/* Reactivate button for expired/declined/cancelled quotes */}
                        {['expired', 'declined', 'cancelled'].includes(req.status) && (
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => openReactivateDialog(req)}
                            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                            title="Reactivate this quote (preserves all files)"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Reactivate
                          </Button>
                        )}
                        
                        {/* Send Quote button - only for pending/reviewing */}
                        {['pending', 'reviewing'].includes(req.status) && (
                          <Button 
                            size="sm"
                            onClick={() => openSendQuote(req)}
                            className="bg-[#CCFF00] text-black hover:bg-[#B3E600]"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Send Quote
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Request Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#CCFF00]" />
              {selectedRequest?.request_id} - Files & Volumes
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-6 mt-4">
              {/* Customer Info */}
              <div className="p-4 bg-white/5 rounded-sm">
                <h3 className="text-sm font-bold text-white/70 mb-2">CUSTOMER</h3>
                <p className="text-white">{selectedRequest.customer_name || 'No name'}</p>
                <p className="text-white/70">{selectedRequest.customer_email}</p>
                {selectedRequest.customer_phone && (
                  <p className="text-white/50">{selectedRequest.customer_phone}</p>
                )}
              </div>

              {/* Files with Volume Entry */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-white/70">
                    {selectedRequest.status === 'ordered' ? 'FILES' : 'FILES - ENTER VOLUMES'}
                  </h3>
                  <div className="flex gap-2">
                    {/* CSV Upload Button - only for non-ordered */}
                    {selectedRequest.status !== 'ordered' && (
                      <>
                        <input
                          type="file"
                          accept=".csv"
                          ref={csvInputRef}
                          onChange={handleCsvUpload}
                          className="hidden"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => csvInputRef.current?.click()}
                          className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                          title="Upload CSV with Part Name and Volume columns"
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-1" />
                          Import CSV
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      onClick={() => downloadAllFiles(selectedRequest.request_id)}
                      className="bg-[#CCFF00] text-black hover:bg-[#B3E600]"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download {selectedRequest.files?.length > 1 ? 'ZIP' : 'File'}
                    </Button>
                  </div>
                </div>
                
                {/* CSV Import Help - only for non-ordered */}
                {selectedRequest.status !== 'ordered' && (
                  <div className="mb-3 p-2 bg-green-500/5 border border-green-500/20 rounded-sm text-xs text-green-400/70">
                    <strong>Tip:</strong> Export volumes from Netfabb as CSV with columns: Part Name, Volume (cm³)
                  </div>
                )}

                <div className="space-y-3">
                  {selectedRequest.files?.map((file, index) => {
                    const pricing = calculateFilePrice(file);
                    return (
                      <div 
                        key={index}
                        className="p-4 bg-white/5 border border-white/10 rounded-sm"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex-1">
                            <p className="text-white font-medium">{file.name}</p>
                            <p className="text-white/50 text-sm">
                              {file.fileType?.toUpperCase()} • Qty: {file.quantity}
                            </p>
                          </div>
                          {pricing.totalPrice > 0 && (
                            <div className="text-right">
                              <p className="text-[#CCFF00] font-mono font-bold">
                                ${pricing.totalPrice.toFixed(2)}
                              </p>
                              <p className="text-white/30 text-xs">
                                ${pricing.unitPrice.toFixed(2)} × {pricing.quantity}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Volume and Material - editable only for non-ordered */}
                        {selectedRequest.status !== 'ordered' ? (
                          <div className="grid grid-cols-2 gap-3">
                            {/* Volume Input */}
                            <div>
                              <Label className="text-white/50 text-xs">Volume (cm³)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={file.volume || ''}
                                onChange={(e) => updateFileVolume(index, e.target.value)}
                                placeholder="0.00"
                                className="bg-yellow-500/10 border-yellow-500/30 text-yellow-400 font-mono mt-1"
                              />
                            </div>
                            
                            {/* Material Selection */}
                            <div>
                              <Label className="text-white/50 text-xs">Material</Label>
                              <Select 
                                value={file.material || ''} 
                                onValueChange={(v) => updateFileMaterial(index, v)}
                              >
                                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                                  <SelectValue placeholder="Select material..." />
                                </SelectTrigger>
                                <SelectContent className="bg-[#111] border-white/10">
                                  {materials.map(mat => (
                                    <SelectItem key={mat.material_id} value={mat.material_id} className="text-white">
                                      {mat.name} (${mat.price_per_cm3}/cm³)
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        ) : (
                          /* Read-only display for ordered quotes */
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <Label className="text-white/50 text-xs">Volume (cm³)</Label>
                              <p className="text-white font-mono mt-1">{file.volume || '—'}</p>
                            </div>
                            <div>
                              <Label className="text-white/50 text-xs">Material</Label>
                              <p className="text-white mt-1">{file.material_name || materials.find(m => m.material_id === file.material)?.name || '—'}</p>
                            </div>
                          </div>
                        )}
                        
                        {file.volumeNeedsManualEntry && !file.volume && selectedRequest.status !== 'ordered' && (
                          <div className="flex items-center gap-2 mt-2 text-yellow-400 text-xs">
                            <AlertTriangle className="w-3 h-3" />
                            Requires manual volume entry
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Linked Order Reference - Show if ordered */}
              {selectedRequest.status === 'ordered' && selectedRequest.order_id && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-400 text-sm font-medium">Order Created</p>
                      <p className="text-white font-mono font-bold">{selectedRequest.order_id}</p>
                    </div>
                    <Link to={`/admin/orders?highlight=${selectedRequest.order_id}`}>
                      <Button
                        size="sm"
                        className="bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-none"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        View Order
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {/* Quote Summary */}
              {(() => {
                const quoteCalc = calculateQuoteTotal();
                // For ordered requests, use stored quote_breakdown if available
                const breakdown = selectedRequest.quote_breakdown;
                const subtotal = breakdown?.subtotal || quoteCalc.subtotal;
                const delivery = breakdown?.delivery || quoteCalc.delivery;
                const total = selectedRequest.quoted_total || breakdown?.total || quoteCalc.total;
                
                return (
                  <div className="p-4 bg-[#CCFF00]/5 border border-[#CCFF00]/20 rounded-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <Calculator className="w-4 h-4 text-[#CCFF00]" />
                      <h3 className="text-sm font-bold text-[#CCFF00]">
                        {selectedRequest.status === 'ordered' ? 'FINAL QUOTE' : 'QUOTE SUMMARY'}
                      </h3>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/70">Subtotal ({selectedRequest.files?.length} parts)</span>
                        <span className="text-white font-mono">${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/70">Delivery</span>
                        <span className="text-white font-mono">${delivery.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-white/10">
                        <span className="text-white font-bold">Total</span>
                        <span className="text-[#CCFF00] font-bold font-mono text-lg">
                          ${total.toFixed(2)} AUD
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="flex justify-between gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDetailsOpen(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Close
                </Button>
                {/* Only show edit actions if not ordered */}
                {selectedRequest.status !== 'ordered' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={saveFileVolumes}
                      disabled={processing}
                      variant="outline"
                      className="border-[#CCFF00]/50 text-[#CCFF00] hover:bg-[#CCFF00]/10"
                    >
                      {processing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Save Quote'
                      )}
                    </Button>
                    <Button
                      onClick={openSendQuoteFromDetails}
                      disabled={calculateQuoteTotal().total <= 0}
                      className="bg-[#CCFF00] text-black hover:bg-[#B3E600]"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Send to Customer
                    </Button>
                  </div>
                )}
                {/* Accept on Behalf button - only for quoted requests */}
                {selectedRequest.status === 'quoted' && (
                  <Button
                    onClick={() => setAcceptOnBehalfOpen(true)}
                    className="bg-orange-500 text-white hover:bg-orange-600"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Accept on Behalf
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Quote Dialog */}
      <Dialog open={sendQuoteOpen} onOpenChange={setSendQuoteOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#CCFF00]" />
              Send Quote to Customer
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-white/5 rounded-sm">
                <p className="text-white/70 text-sm">Sending to:</p>
                <p className="text-white font-medium">{selectedRequest.customer_email}</p>
              </div>

              {/* Quote Breakdown Preview */}
              <div className="border border-white/10 rounded-sm overflow-hidden">
                <div className="bg-white/5 px-4 py-2 border-b border-white/10">
                  <h4 className="text-sm font-bold text-white/70">QUOTE BREAKDOWN (will be included in email)</h4>
                </div>
                
                {/* Parts Table */}
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 sticky top-0">
                      <tr className="text-white/50 text-xs">
                        <th className="text-left p-2">Part Name</th>
                        <th className="text-right p-2">Volume</th>
                        <th className="text-center p-2">Qty</th>
                        <th className="text-right p-2">Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {(() => {
                        const quoteCalc = calculateQuoteTotal();
                        return quoteCalc.items.map((item, idx) => (
                          <tr key={idx} className="text-white/80">
                            <td className="p-2 truncate max-w-[150px]" title={item.name}>{item.name}</td>
                            <td className="p-2 text-right font-mono text-xs">{item.pricing.volume.toFixed(2)} cm³</td>
                            <td className="p-2 text-center">{item.pricing.quantity}</td>
                            <td className="p-2 text-right font-mono text-[#CCFF00]">${item.pricing.totalPrice.toFixed(2)}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
                
                {/* Totals */}
                <div className="bg-white/5 p-3 border-t border-white/10">
                  {(() => {
                    const quoteCalc = calculateQuoteTotal();
                    return (
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between text-white/50">
                          <span>Subtotal</span>
                          <span className="font-mono">${quoteCalc.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-white/50">
                          <span>Delivery</span>
                          <span className="font-mono">${quoteCalc.delivery.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div>
                <Label className="text-white/70">Final Quote Total (AUD) *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={quoteData.total}
                    onChange={(e) => setQuoteData(prev => ({ ...prev, total: e.target.value }))}
                    placeholder="0.00"
                    className="bg-white/5 border-white/10 text-white pl-8 font-mono text-xl font-bold"
                  />
                </div>
                <p className="text-white/30 text-xs mt-1">You can adjust the total if needed</p>
              </div>

              <div>
                <Label className="text-white/70">Message to Customer (Optional)</Label>
                <Textarea
                  value={quoteData.message}
                  onChange={(e) => setQuoteData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Any additional notes about the quote..."
                  className="bg-white/5 border-white/10 text-white mt-1 min-h-[60px]"
                />
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 rounded-sm">
                <p className="text-yellow-400 text-xs">
                  <strong>Note:</strong> Customer will receive an HTML email with the full breakdown above. 
                  PDF quotes with part thumbnails coming soon.
                </p>
              </div>

              <Button
                onClick={sendQuote}
                disabled={processing || !quoteData.total}
                className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Quote Email
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Accept on Behalf Dialog */}
      <Dialog open={acceptOnBehalfOpen} onOpenChange={setAcceptOnBehalfOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-400">
              <CheckCircle className="w-5 h-5" />
              Accept Quote on Behalf of Customer
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-orange-300 text-sm mb-2">
                  <strong>⚠️ Important:</strong> You are accepting this quote on behalf of the customer. 
                  An invoice will be automatically sent to their email.
                </p>
                <p className="text-white/70 text-sm">
                  Customer: <strong className="text-white">{selectedRequest.customer_name}</strong><br/>
                  Email: <strong className="text-white">{selectedRequest.customer_email}</strong><br/>
                  Total: <strong className="text-[#CCFF00]">${selectedRequest.quoted_total?.toFixed(2)} AUD</strong>
                </p>
              </div>

              <div>
                <Label className="text-white/70">Customer PO Number (optional)</Label>
                <Input
                  value={acceptPoNumber}
                  onChange={(e) => setAcceptPoNumber(e.target.value)}
                  placeholder="e.g., PO-12345"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
                <p className="text-white/50 text-xs mt-1">Will appear on the invoice</p>
              </div>

              <div>
                <Label className="text-white/70">Admin Notes (optional)</Label>
                <Textarea
                  value={acceptNotes}
                  onChange={(e) => setAcceptNotes(e.target.value)}
                  placeholder="e.g., Customer confirmed via phone call on [date]"
                  className="bg-white/5 border-white/10 text-white mt-1"
                  rows={3}
                />
              </div>

              <div>
                <Label className="text-white/70">Proof of Confirmation (optional but recommended)</Label>
                <p className="text-white/50 text-xs mb-2">Upload a screenshot of email, message, or other confirmation</p>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.pdf"
                    onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="proof-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                    onClick={() => document.getElementById('proof-file-input').click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {proofFile ? 'Change File' : 'Upload Proof'}
                  </Button>
                  {proofFile && (
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <FileText className="w-4 h-4 text-[#CCFF00]" />
                      <span className="truncate max-w-[150px]">{proofFile.name}</span>
                      <button 
                        onClick={() => setProofFile(null)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setAcceptOnBehalfOpen(false);
                    setAcceptNotes('');
                    setAcceptPoNumber('');
                    setProofFile(null);
                  }}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAcceptOnBehalf}
                  disabled={processing}
                  className="flex-1 bg-orange-500 text-white hover:bg-orange-600 font-bold"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Accept & Send Invoice
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reactivate Quote Dialog */}
      <Dialog open={reactivateDialogOpen} onOpenChange={setReactivateDialogOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-cyan-400" />
              Reactivate Quote
            </DialogTitle>
          </DialogHeader>
          
          {reactivateRequest && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                <p className="text-white font-medium">{reactivateRequest.request_id}</p>
                <p className="text-white/50 text-sm">{reactivateRequest.customer_name} - {reactivateRequest.customer_email}</p>
                <p className="text-white/50 text-sm">{reactivateRequest.files?.length || 0} file(s) preserved</p>
              </div>
              
              <p className="text-white/70 text-sm">
                Reactivating will set this quote back to &quot;Quoted&quot; status with a new 30-day expiry. 
                All uploaded files are preserved.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-lg">
                  <Checkbox 
                    id="update-price"
                    checked={updatePrice}
                    onCheckedChange={setUpdatePrice}
                    className="border-cyan-500 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                  />
                  <Label htmlFor="update-price" className="text-white cursor-pointer">
                    Update quoted price
                  </Label>
                </div>
                
                {updatePrice && (
                  <div className="space-y-2">
                    <Label className="text-white/70">New Quote Total (excl. GST)</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-white/50">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={reactivatePrice}
                        onChange={(e) => setReactivatePrice(e.target.value)}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="0.00"
                      />
                    </div>
                    <p className="text-white/50 text-xs">
                      Previous price: ${reactivateRequest.quoted_total?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setReactivateDialogOpen(false)}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  disabled={reactivating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={reactivateQuote}
                  disabled={reactivating || (updatePrice && !reactivatePrice)}
                  className="flex-1 bg-cyan-500 text-white hover:bg-cyan-600"
                >
                  {reactivating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Reactivating...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reactivate Quote
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminQuoteRequests;
